import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
} from '../Base.js'
import { getFullHourString,
  generateItemName,
  sortItemsByName,
} from '../SharedUtils.js'

import { Field, FieldType, FieldGroup, } from '../Common/Field.js'

import { gApp, DebugOn } from '../Globals.js'

export class Schedule {
  init(name, makeId) {
    this.name = Field.makeName(`Schedule Name`, name)
    this.id = makeId ? gApp.proj().makeId('Schedule') : 0;
    this.editable = true;

    let fields = {}
    for (let i = 0; i < 24; i++) {
      fields[`${i}`] = new Field({
        name: getFullHourString(i),
        type: FieldType.Percent,
        defaultValue: 100.0,
        note: `Percentage of full operation during this hour. 0% means fully off, 100% means full operation. Values in between are interpreted as a fractional load.`,
      });
    }
    this.hourFields = FieldGroup.fromDict(fields)

    this.serFields = [
      'name',
      'id',
      'editable',
      'hourFields',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _uniqueName: true,
      _name: () => {
        return this.name.value;
      },
      uniquePath: true,
      path: () => {
        return `schedules/${this.id}`;
      },
    }
  }

  static getTypeMetadata() {
    return {
      name: 'Schedule',
      createType: Schedule.createType,
      openEditor: Schedule.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = Schedule.create(generateItemName('Schedule', gApp.proj().schedules), true)
    gApp.proj().schedules.push(type);
    sortItemsByName(gApp.proj().schedules);
    return type
  }

  static openEditor(type) {
    let pathName = 'schedules'
    gApp.proj().editorState.selectedSchedule = type;
    gApp.router.push({name: pathName});
  }

  setFromExcelData(data, importer) {
    // Set name
    importer.applyData(data, {
      'Name': (value) => {
        this.name.setValueAsUser(value);
      }
    }); 

    // Set hours
    let fieldsMap = {
      '12am': 0,
      '1am': 1,
      '2am': 2,
      '3am': 3,
      '4am': 4,
      '5am': 5,
      '6am': 6,
      '7am': 7,
      '8am': 8,
      '9am': 9,
      '10am': 10,
      '11am': 11,
      '12pm': 12,
      '1pm': 13,
      '2pm': 14,
      '3pm': 15,
      '4pm': 16,
      '5pm': 17,
      '6pm': 18,
      '7pm': 19,
      '8pm': 20,
      '9pm': 21,
      '10pm': 22,
      '11pm': 23,
    }
    for (const key in fieldsMap) {
      if (data.hasOwnProperty(key)) {
        let hour = fieldsMap[key];
        let val = parseFloat(data[key]);
        if (!isNaN(val)) {
          // Clamp to 0-100
          val = Math.max(0, Math.min(100, val));
          this.setHour(hour, val);
        }
      }
    }
  }

  isEditable() {
    return this.editable;
  }

  setEditable(editable) {
    this.editable = editable;
  }

  dumpValues() {
    console.log("Schedule values:");
    let fields = this.hourFields.fields.slice(12);
    for (const field of fields) {
      console.log(`${field.name} (${field.key}): ${field.value}`);
    }
  }

  static createDefaultSchedules() {
    // Create the schedules that will exist by default in new projects

    let alwaysOn = Schedule.create("AlwaysOn", true);
    for (let i = 0; i < 24; i++) {
      alwaysOn.hourFields.get(`${i}`).value = 100;
    }
    alwaysOn.setEditable(false);

    let onDuringWorkHours = Schedule.create("WorkHours", true);
    for (let i = 0; i < 24; i++) {
      onDuringWorkHours.hourFields.get(`${i}`).value = 0;
    }
    for (let i = 9; i < 17; i++) {
      onDuringWorkHours.hourFields.get(`${i}`).value = 100;
    }
    // Allow editing this one
    onDuringWorkHours.setEditable(true);

    return [alwaysOn, onDuringWorkHours];
  }

  getInputPage() {
    return {
      label: `${this.name.value}`,
      // TODO
      path: `schedules/${this.id}`,
      typeName: 'Schedule',
      typeObject: this,
    };
  }

  getHour(i) {
    return this.hourFields.get(`${i}`).value;
  }

  setHour(i, value) {
    this.hourFields.get(`${i}`).value = value;
  }

  getData() {
    let data = []
    for (let i = 0; i < 24; i++) {
      data.push(this.getHour(i) / 100.0);
    }
    return data;
  }

  static getTableInfo() {
    return {
      typeName: 'Schedule',
      pagePath: 'schedules',
      allowDuplicate: true,
      // Note: the duplicate should be editable
      excludeFieldsForDuplicate: ['editable'],
      columns: {
        'name': {
          label: 'Name',
        }
      }
    }
  }

  getTableData() {
    return {
      name: this.name.value,
    }
  }
}
setupClass(Schedule)
